/**
 * @imagine-readonly
 */

import { useMutation } from '@tanstack/react-query'
import {
  createFileRoute,
  Link,
  useNavigate,
  useRouter,
  useSearch,
} from '@tanstack/react-router'
import { z } from 'zod'
import { AuthCard } from '@/components/auth/auth-card'
import { AuthForm } from '@/components/auth/auth-form'
import { AuthField } from '@/components/auth/auth-field'
import { sendMagicLinkFn, signInFn } from '@/server/functions/auth'
import { useServerFn } from '@tanstack/react-start'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { APP_NAME, APP_VERSION } from '@/lib/constants'
import { Button } from '@/components/ui/button'
import { useState } from 'react'

const searchSchema = z.object({
  redirect: z.string().optional(),
})

export const Route = createFileRoute('/_auth/sign-in')({
  component: SignInPage,
  validateSearch: searchSchema,
})

const signInSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
})

function SignInPage() {
  const search = useSearch({ from: '/_auth/sign-in' })
  const navigate = useNavigate()
  const router = useRouter()
  const signIn = useServerFn(signInFn)
  const sendMagicLink = useServerFn(sendMagicLinkFn)
  const [magicLinkStatus, setMagicLinkStatus] = useState<string | null>(null)
  const form = useForm({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const signInMutation = useMutation({
    mutationFn: async (data: z.infer<typeof signInSchema>) => {
      await signIn({
        data: { ...data, redirect: search.redirect },
      })
    },
    onSuccess: async () => {
      // Invalidate router to refresh auth state
      await router.invalidate()
      // Navigate to the redirect destination if provided
      if (search.redirect) {
        await navigate({ to: search.redirect })
      }
    },

    onError: async (error: {
      status: number
      redirect: boolean
      message: string
    }) => {
      // Check if it's a redirect error (TanStack Start throws redirects as errors)
      if (
        error?.status === 302 ||
        error?.redirect ||
        error?.message?.includes('redirect')
      ) {
        // Invalidate router to refresh auth state
        await router.invalidate()
        // Navigate to the redirect destination if provided
        if (search.redirect) {
          await navigate({ to: search.redirect })
        }
        return
      }
      console.error('Sign in error:', error)
      form.setError('root', { message: error.message || 'Failed to sign in' })
    },
  })

  const magicLinkMutation = useMutation({
    mutationFn: async () => {
      const isValid = await form.trigger('email')
      if (!isValid) {
        throw new Error('Please enter a valid email address')
      }

      const email = form.getValues('email')
      await sendMagicLink({
        data: {
          email,
          redirect: search.redirect,
        },
      })
    },
    onSuccess: () => {
      setMagicLinkStatus('Magic link sent. Check your inbox.')
    },
    onError: (error: { message?: string }) => {
      setMagicLinkStatus(error?.message || 'Failed to send magic link.')
    },
  })

  const handleGoogleSignIn = () => {
    const endpoint = import.meta.env.VITE_APPWRITE_ENDPOINT
    const projectId = import.meta.env.VITE_APPWRITE_PROJECT_ID

    if (!endpoint || !projectId) {
      form.setError('root', {
        message: 'Appwrite OAuth is not configured.',
      })
      return
    }

    const origin = window.location.origin
    const success = new URL(`${origin}/callback`)
    if (search.redirect) {
      success.searchParams.set('redirect', search.redirect)
    }

    const failure = new URL(`${origin}/sign-in`)
    if (search.redirect) {
      failure.searchParams.set('redirect', search.redirect)
    }
    failure.searchParams.set('error', 'oauth')

    const oauthUrl = `${endpoint}/account/sessions/oauth2/google?project=${encodeURIComponent(
      projectId,
    )}&success=${encodeURIComponent(success.toString())}&failure=${encodeURIComponent(
      failure.toString(),
    )}`

    window.location.assign(oauthUrl)
  }

  return (
    <>
      <AuthCard
        title="Sign in"
        description="Enter your email and password to access your account"
      >
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={handleGoogleSignIn}
        >
          Continue with Google
        </Button>
        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-slate-200" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-white px-2 text-slate-400">Or</span>
          </div>
        </div>
        <AuthForm
          schema={signInSchema}
          defaultValues={{
            email: '',
            password: '',
          }}
          onSubmit={(data) => signInMutation.mutate(data)}
          submitText="Sign in"
          loadingText="Signing in..."
          isLoading={signInMutation.isPending}
          form={form}
        >
          {(form) => (
            <>
              <AuthField
                control={form.control}
                name="email"
                label="Email"
                placeholder="john@doe.com"
                type="email"
              />

              <div className="space-y-2">
                <AuthField
                  control={form.control}
                  name="password"
                  label="Password"
                  placeholder="Enter your password"
                  type="password"
                />
                <div className="text-right">
                  <Link
                    to="/forgot-password"
                    className="text-xs md:text-sm text-muted-foreground hover:text-primary underline-offset-4 hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>
            </>
          )}
        </AuthForm>
        <div className="mt-4 space-y-2">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => magicLinkMutation.mutate()}
            disabled={magicLinkMutation.isPending}
          >
            {magicLinkMutation.isPending ? 'Sending link...' : 'Send Magic Link'}
          </Button>
          {magicLinkStatus ? (
            <p className="text-xs text-slate-500 text-center">
              {magicLinkStatus}
            </p>
          ) : null}
        </div>

        <div className="text-center text-sm text-muted-foreground mt-4 space-x-1">
          <div className="inline-block">Don't have an account? </div>
          <div className="inline-block">
            <Link
              to="/sign-up"
              search={search.redirect ? { redirect: search.redirect } : undefined}
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              Sign up
            </Link>
          </div>
        </div>
      </AuthCard>

      {/* Footer */}
      <footer className="py-8 absolute bottom-0 w-full">
        <div className="container mx-auto px-4 text-center text-slate-600 text-sm">
          <p>© 2026 {APP_NAME} - v{APP_VERSION}. All rights reserved.</p>
        </div>
      </footer>
    </>
  )
}
